import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";
import { parseMoney, formatMoneyDisplay, formatMoneyInput } from "../../utils/money";
import Button from "../common/Button";

const GST_RATE = 0.1;

/**
 * QuoteTemplatesPanel — CRUD for tradie's reusable quote templates.
 *
 * 2026-04-30: merged with QuoteBuilder's UX. A template now holds an array of
 * line items (description + amount) that mirrors a real quote, rendered with
 * the same `.line-item-row` + `.totals-table` styling so the editing surface
 * feels identical.
 *
 * @param {Object}  props
 * @param {string}  props.businessId
 * @param {Object}  [props.profile]
 */
export default function QuoteTemplatesPanel({ businessId, profile }) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [draftLineItems, setDraftLineItems] = useState([
    { _id: 1, description: "", amount: 0 },
  ]);
  const [saving, setSaving] = useState(false);

  const idCounter = React.useRef(1);
  function nextId() { idCounter.current += 1; return idCounter.current; }

  const gstRegistered = profile?.gst_registered !== false;

  const loadTemplates = useCallback(async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("quote_templates")
      .select("id, name, description, amount, line_items, sort_order, created_at")
      .eq("business_id", businessId)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) {
      showToast(`Couldn't load templates: ${error.message}`, "error");
    } else {
      const normalised = (data || []).map((t) => ({
        ...t,
        line_items:
          Array.isArray(t.line_items) && t.line_items.length > 0
            ? t.line_items
            : [
                {
                  description: t.description || t.name || "",
                  amount: Number(t.amount) || 0,
                },
              ],
      }));
      setTemplates(normalised);
    }
    setLoading(false);
  }, [businessId, showToast]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  function resetDraft() {
    setEditingId(null);
    setDraftName("");
    setDraftLineItems([{ _id: nextId(), description: "", amount: 0 }]);
  }

  function startNew() {
    setEditingId("new");
    setDraftName("");
    setDraftLineItems([{ _id: nextId(), description: "Labour", amount: 0 }]);
  }

  function startEdit(t) {
    setEditingId(t.id);
    setDraftName(t.name || "");
    const items = (t.line_items || []).map((it) => ({
      _id: nextId(),
      description: it.description || "",
      amount: parseMoney(it.amount),
    }));
    setDraftLineItems(items.length ? items : [{ _id: nextId(), description: "", amount: 0 }]);
  }

  function addDraftLineItem() {
    setDraftLineItems((prev) => [
      ...prev,
      { _id: nextId(), description: "", amount: 0 },
    ]);
  }

  function updateDraftLineItem(index, field, value) {
    setDraftLineItems((prev) =>
      prev.map((it, i) =>
        i !== index
          ? it
          : field === "amount"
          ? { ...it, amount: parseMoney(value) }
          : { ...it, [field]: value }
      )
    );
  }

  function removeDraftLineItem(index) {
    setDraftLineItems((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );
  }

  const draftSubtotal = useMemo(
    () => draftLineItems.reduce((sum, it) => sum + (parseMoney(it.amount) || 0), 0),
    [draftLineItems]
  );
  const draftGst = gstRegistered ? draftSubtotal * GST_RATE : 0;
  const draftTotal = draftSubtotal + draftGst;

  async function handleSave() {
    const name = draftName.trim();
    if (!name) {
      showToast("Template needs a name.", "error");
      return;
    }
    if (!businessId) {
      showToast("Business profile not loaded yet.", "error");
      return;
    }
    const cleanedItems = draftLineItems
      .map((it) => ({
        description: (it.description || "").trim(),
        amount: parseMoney(it.amount),
      }))
      .filter((it) => it.description || it.amount);
    if (cleanedItems.length === 0) {
      showToast("Add at least one line item with a description or amount.", "error");
      return;
    }

    setSaving(true);
    const legacyDescription =
      cleanedItems.length === 1
        ? cleanedItems[0].description
        : cleanedItems.map((it) => `• ${it.description}`).join("\n");
    const legacyAmount = cleanedItems.reduce((s, it) => s + it.amount, 0);

    const payload = {
      business_id: businessId,
      name,
      description: legacyDescription || null,
      amount: legacyAmount,
      line_items: cleanedItems,
    };

    let result;
    if (editingId === "new") {
      result = await supabase
        .from("quote_templates")
        .insert(payload)
        .select()
        .single();
    } else {
      result = await supabase
        .from("quote_templates")
        .update({
          name: payload.name,
          description: payload.description,
          amount: payload.amount,
          line_items: payload.line_items,
        })
        .eq("id", editingId)
        .select()
        .single();
    }

    setSaving(false);

    if (result.error) {
      showToast(`Save failed: ${result.error.message}`, "error");
      return;
    }

    showToast(
      editingId === "new" ? "Template added." : "Template updated.",
      "success"
    );
    resetDraft();
    loadTemplates();
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this template? This can't be undone.")) return;
    const { error } = await supabase
      .from("quote_templates")
      .delete()
      .eq("id", id);
    if (error) {
      showToast(`Delete failed: ${error.message}`, "error");
      return;
    }
    showToast("Template deleted.", "success");
    loadTemplates();
  }

  const totalCount = useMemo(() => templates.length, [templates]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          marginBottom: "12px",
          flexWrap: "wrap",
        }}
      >
        <p className="muted small" style={{ margin: 0 }}>
          {loading
            ? "Loading…"
            : totalCount === 0
            ? "No templates yet — create your first one to make quoting one-tap fast."
            : `${totalCount} template${totalCount === 1 ? "" : "s"} saved`}
        </p>
        {editingId !== "new" && (
          <Button onClick={startNew} disabled={loading || saving}>
            <Plus size={16} /> Add template
          </Button>
        )}
      </div>

      {editingId && (
        <div
          className="quote-edit-zone"
          style={{
            border: "1px solid var(--hairline, var(--border, rgba(140,100,80,0.18)))",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "16px",
            background: "var(--surface-2, var(--surface-soft, #faf5f2))",
          }}
        >
          <div className="form-grid" style={{ gap: "10px", marginBottom: "12px" }}>
            <div className="full">
              <label>Template name *</label>
              <input
                type="text"
                placeholder="e.g. Hot water install — 4hr labour + materials"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          <div
            className="line-item-row"
            style={{
              marginBottom: "6px",
              fontSize: "0.72rem",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--text-soft, #6b5448)",
              fontWeight: 600,
            }}
          >
            <span style={{ flex: 1, paddingLeft: "2px" }}>Description</span>
            <span style={{ width: "120px", flexShrink: 0, textAlign: "right" }}>
              {gstRegistered ? "Amount (ex GST)" : "Amount"}
            </span>
            <span style={{ visibility: "hidden", flexShrink: 0 }} aria-hidden>
              Remove
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
            {draftLineItems.map((item, index) => (
              <div key={item._id} className="line-item-row">
                <input
                  type="text"
                  placeholder="Description (e.g. 4 hrs labour)"
                  value={item.description}
                  onChange={(e) => updateDraftLineItem(index, "description", e.target.value)}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={formatMoneyInput(item.amount)}
                  onChange={(e) => updateDraftLineItem(index, "amount", e.target.value)}
                />
                <Button
                  secondary
                  onClick={() => removeDraftLineItem(index)}
                  disabled={saving || draftLineItems.length === 1}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>

          <div className="button-row" style={{ marginBottom: "16px" }}>
            <Button secondary onClick={addDraftLineItem} disabled={saving}>
              + Add line item
            </Button>
          </div>

          <div className="totals-table">
            {gstRegistered ? (
              <>
                <div className="totals-row">
                  <span className="totals-label">Subtotal (ex GST)</span>
                  <span className="totals-amount">{formatMoneyDisplay(draftSubtotal)}</span>
                </div>
                <div className="totals-row">
                  <span className="totals-label">GST (10%)</span>
                  <span className="totals-amount">{formatMoneyDisplay(draftGst)}</span>
                </div>
                <div className="totals-row total">
                  <span className="totals-label">Total (inc GST)</span>
                  <span className="totals-amount">{formatMoneyDisplay(draftTotal)}</span>
                </div>
              </>
            ) : (
              <div className="totals-row total">
                <span className="totals-label">Total</span>
                <span className="totals-amount">{formatMoneyDisplay(draftTotal)}</span>
              </div>
            )}
          </div>

          <div className="button-row" style={{ marginTop: "12px" }}>
            <Button onClick={handleSave} disabled={saving}>
              <Check size={16} /> {saving ? "Saving…" : "Save template"}
            </Button>
            <Button secondary onClick={resetDraft} disabled={saving}>
              <X size={16} /> Cancel
            </Button>
          </div>
        </div>
      )}

      {!loading && templates.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {templates.map((t) => {
            const lineCount = (t.line_items || []).length;
            const subtotal = (t.line_items || []).reduce(
              (s, it) => s + (Number(it.amount) || 0),
              0
            );
            return (
              <div
                key={t.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  padding: "12px 14px",
                  border: "1px solid var(--hairline, var(--border, rgba(140,100,80,0.14)))",
                  borderRadius: "12px",
                  background: "var(--surface, #fff)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        color: "var(--text)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t.name}
                    </div>
                    <div
                      style={{
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        color: "var(--text)",
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatMoneyDisplay(subtotal)}
                    </div>
                  </div>
                  <div className="muted small" style={{ marginTop: "4px" }}>
                    {lineCount === 1
                      ? (t.line_items[0]?.description || "1 line item")
                      : `${lineCount} line items`}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    flexShrink: 0,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => startEdit(t)}
                    className="btn btn-secondary"
                    style={{ padding: "6px 10px" }}
                    aria-label={`Edit ${t.name}`}
                    disabled={!!editingId}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(t.id)}
                    className="btn btn-secondary"
                    style={{
                      padding: "6px 10px",
                      color: "#dc2626",
                    }}
                    aria-label={`Delete ${t.name}`}
                    disabled={!!editingId}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && templates.length === 0 && !editingId && (
        <div
          style={{
            padding: "24px 0",
            textAlign: "center",
            color: "var(--text-soft, #6b5448)",
          }}
        >
          <div style={{ fontSize: "2rem", marginBottom: "6px" }}>📋</div>
          <p
            className="muted small"
            style={{ margin: 0, maxWidth: "440px", marginInline: "auto" }}
          >
            Templates are reusable quote bundles — labour rates, common
            installs, set-fee jobs. Save once, drop them into quotes from
            then on.
          </p>
        </div>
      )}
    </div>
  );
}
